import React from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';

// What the suggestion would put in place: new text, or the new ticket's title.
function afterText(change) {
  if (!change) return '';
  if (change.op === 'add_ticket') return (change.newTicket && change.newTicket.title) || '';
  if (change.op === 'remove_row') return '(removed)';
  if (change.op === 'move_row') return `(moved to ticket ${change.toTicket})`;
  return change.value || '';
}

/**
 * ReviewPanel - steer box + Run review + the suggestion list. Presentational:
 * the page owns the request and the doc. `resolve(s)` -> { ok, reason } is
 * re-run every render; an unresolvable suggestion shows its reason and Accept
 * is disabled.
 */
export default function ReviewPanel({
  open, instruction, onInstruction, onRun, running, disabled,
  suggestions, onReject, onAccept, resolve, accepting,
}) {
  if (!open && !(suggestions && suggestions.length)) return null;
  return (
    <Box sx={{ mb: 2 }} data-testid="review-panel">
      {open && (
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Optional focus, e.g. the app builds the IU itself, add a client ticket"
            value={instruction}
            onChange={(e) => onInstruction(e.target.value)}
            disabled={running}
          />
          <Button
            variant="contained"
            size="small"
            onClick={onRun}
            disabled={disabled || running}
            title={disabled ? 'Save first' : undefined}
            startIcon={running ? <CircularProgress size={14} color="inherit" /> : null}
          >
            Run review
          </Button>
        </Stack>
      )}
      {suggestions && suggestions.length > 0 && (
        <Stack spacing={1}>
          {suggestions.map((s, i) => {
            const after = afterText(s.change);
            const res = resolve ? resolve(s) : null;
            return (
              <Paper key={s.id || i} variant="outlined" sx={{ p: 1 }} data-testid={`suggestion-${i}`}>
                <Stack direction="row" spacing={1} alignItems="flex-start">
                  <Chip size="small" variant="outlined" label={s.kind} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">{s.rationale}</Typography>
                    {(s.anchor || after) && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {s.anchor ? `Before: ${s.anchor}` : 'Before: (none)'} {'->'} {`After: ${after}`}
                      </Typography>
                    )}
                  </Box>
                  {res && !res.ok && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      title={res.reason === 'target changed' ? 'The row this suggestion points at was edited. Run Review again.' : undefined}
                    >
                      {res.reason}
                    </Typography>
                  )}
                  <Button
                    size="small"
                    disabled={!onAccept || accepting || (res ? !res.ok : false)}
                    onClick={onAccept ? () => onAccept(s, i) : undefined}
                  >
                    Accept
                  </Button>
                  <Button size="small" onClick={() => onReject(i)}>Reject</Button>
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}
